import * as React from "react";
import type {
    FirstPrincipleAnswer,
    FirstPrincipleQuestion,
    FirstPrinciplesPublicBundle,
    FirstPrincipleState,
    PublicSubmitAnswersPayload,
    PublicSubmitAnswersResponse,
    PublicSubmitAudioPayload,
    AudioAnswerDraft,
} from "./types";
import { firstPrinciplesPublicApi } from "../../api/first-principles/first-principles";
import { clearFpCookie, upsertFpCookie } from "./utils/fp-cookie";

function sortQuestions(items: FirstPrincipleQuestion[]) {
    return [...items].sort((a, b) => {
        const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (orderDiff !== 0) return orderDiff;

        const at = new Date(a.created_at).getTime();
        const bt = new Date(b.created_at).getTime();
        return at - bt;
    });
}

function clampIndex(i: number, total: number) {
    if (!Number.isFinite(total) || total <= 0) return 0;
    if (!Number.isFinite(i)) return 0;
    return Math.max(0, Math.min(total - 1, i));
}

const MAX_AUDIO_UPLOAD_BYTES = 12 * 1024 * 1024; // 12MB

function makeClientUploadId(questionId: string) {
    return `${questionId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useFirstPrinciplesChat() {
    const [invitationCode, _setInvitationCode] = React.useState("");

    const setInvitationCode = React.useCallback((v: string) => {
        const next = String(v ?? "");
        _setInvitationCode(next);

        const trimmed = next.trim();
        if (!trimmed) {
            clearFpCookie();
            return;
        }

        upsertFpCookie({ invitationCode: trimmed });
    }, []);

    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const [error, setError] = React.useState<string | null>(null);
    const [success, setSuccess] = React.useState<string | null>(null);

    const [state, setState] = React.useState<FirstPrincipleState | null>(null);
    const [questions, setQuestions] = React.useState<FirstPrincipleQuestion[]>([]);
    const [_serverAnswers, setServerAnswers] = React.useState<FirstPrincipleAnswer[]>([]);
    const [answers, setAnswers] = React.useState<Record<string, string>>({});

    const [audioDrafts, setAudioDrafts] = React.useState<
        Record<string, AudioAnswerDraft | null>
    >({});
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [showReview, setShowReview] = React.useState(false);

    const isLoaded = Boolean(state) && questions.length > 0;

    const isLastQuestion = isLoaded && currentIndex === questions.length - 1;

    const currentQuestion = React.useMemo(() => {
        if (!isLoaded) return null;
        return questions[currentIndex] ?? null;
    }, [isLoaded, questions, currentIndex]);

    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < questions.length - 1;

    const setAnswer = React.useCallback((questionId: string, value: string) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }, []);

    const attachAudioToQuestion = React.useCallback(
        (questionId: string, blob: Blob, transcript?: string) => {
            setAudioDrafts((prev) => {
                const existing = prev[questionId];
                if (existing?.url) {
                    try { URL.revokeObjectURL(existing.url); } catch { /* ignore */ }
                }

                const url = URL.createObjectURL(blob);
                const t = transcript?.trim();

                const nextDraft: AudioAnswerDraft = t
                    ? { url, blob, transcript: t, createdAt: Date.now() }
                    : { url, blob, createdAt: Date.now() };

                return { ...prev, [questionId]: nextDraft };
            });
        },
        [],
    );

    const clearAudioForQuestion = React.useCallback((questionId: string) => {
        setAudioDrafts((prev) => {
            const existing = prev[questionId];
            if (existing?.url) {
                try { URL.revokeObjectURL(existing.url); } catch { /* ignore */ }
            }
            return { ...prev, [questionId]: null };
        });
    }, []);

    const load = React.useCallback(async (code: string) => {
        const trimmed = code.trim();
        if (!trimmed) {
            setError("");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setShowReview(false);

        try {
            const bundle: FirstPrinciplesPublicBundle =
                await firstPrinciplesPublicApi.getBundle(trimmed);
            const sorted = sortQuestions(bundle.questions);

            if (sorted.length === 0) {
                setError("Your first principles are being prepared. Please check back later.");
                setLoading(false);
                return;
            }

            setState(bundle.state);
            setQuestions(sorted);
            setServerAnswers(bundle.answers ?? []);

            const serverMap: Record<string, string> = {};
            for (const a of bundle.answers ?? []) {
                serverMap[a.question_id] = a.answer ?? "";
            }
            setAnswers(serverMap);

            setInvitationCode(trimmed);

            const idxFromServer = clampIndex(
                bundle.state?.current_index ?? 0,
                sorted.length,
            );
            setCurrentIndex(idxFromServer);

            // If already completed, go straight to review
            if (bundle.state?.is_completed) {
                setShowReview(true);
            }

            upsertFpCookie({
                invitationCode: trimmed,
                lastIndex: idxFromServer,
            });
        } catch (e) {
            console.error("Failed to load FP bundle", e);
            setError(
                "Unable to load your first principles. Please check the code and try again.",
            );
            setState(null);
            setQuestions([]);
            setServerAnswers([]);
            setAnswers({});
            setAudioDrafts({});
            setCurrentIndex(0);
        } finally {
            setLoading(false);
        }
    }, [setInvitationCode]);

    // cleanup audio urls
    React.useEffect(() => {
        return () => {
            try {
                Object.values(audioDrafts).forEach((d) => {
                    if (d?.url) URL.revokeObjectURL(d.url);
                });
            } catch { /* ignore */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitPartial = React.useCallback(
        async (
            payload: PublicSubmitAnswersPayload,
        ): Promise<PublicSubmitAnswersResponse | null> => {
            const code = invitationCode.trim();
            if (!code) {
                setError("Missing invitation code.");
                return null;
            }

            setSubmitting(true);
            setError(null);
            setSuccess(null);

            try {
                const res = await firstPrinciplesPublicApi.submitAnswers(code, payload);

                setState(res.state);
                setServerAnswers(res.answers);

                const nextMap: Record<string, string> = {};
                for (const a of res.answers ?? []) {
                    nextMap[a.question_id] = a.answer ?? "";
                }
                setAnswers((prev) => ({ ...prev, ...nextMap }));

                const idx = res.state?.current_index;
                if (typeof idx === "number" && Number.isFinite(idx)) {
                    upsertFpCookie({ invitationCode: code, lastIndex: idx });
                } else {
                    upsertFpCookie({ invitationCode: code });
                }

                setSuccess("");
                return res;
            } catch (e) {
                console.error("Failed to submit FP answer/progress", e);
                setError("Unable to submit. Please try again.");
                return null;
            } finally {
                setSubmitting(false);
            }
        },
        [invitationCode],
    );

    const submitAudio = React.useCallback(
        async (payload: PublicSubmitAudioPayload) => {
            const code = invitationCode.trim();
            if (!code) {
                setError("Missing invitation code.");
                return null;
            }

            setSubmitting(true);
            setError(null);
            setSuccess(null);

            try {
                const args: {
                    invitationCode: string;
                    questionId: string;
                    audioBlob?: Blob;
                    transcriptText?: string;
                    currentIndex?: number;
                    clientUploadId?: string;
                } = {
                    invitationCode: code,
                    questionId: payload.question_id,
                    clientUploadId: makeClientUploadId(payload.question_id),
                };

                if (payload.audio_blob) args.audioBlob = payload.audio_blob;

                const t = typeof payload.transcript_text === "string"
                    ? payload.transcript_text.trim()
                    : "";
                if (t) args.transcriptText = t;

                if (
                    typeof payload.current_index === "number" &&
                    Number.isFinite(payload.current_index)
                ) {
                    args.currentIndex = payload.current_index;
                }

                const res = await firstPrinciplesPublicApi.submitAudioAnswer(args);

                if (!res.ok) {
                    setError(res.message || "Unable to submit audio. Please try again.");
                    return null;
                }

                setState(res.data.state);
                setServerAnswers(res.data.answers);

                if (args.transcriptText) {
                    setAnswers((prev) => ({
                        ...prev,
                        [payload.question_id]: args.transcriptText!,
                    }));
                }

                const idx = res.data.state?.current_index;
                if (typeof idx === "number" && Number.isFinite(idx)) {
                    upsertFpCookie({ invitationCode: code, lastIndex: idx });
                } else {
                    upsertFpCookie({ invitationCode: code });
                }

                setSuccess("");
                return res.data;
            } catch (e) {
                console.error("Failed to submit FP audio/progress", e);
                setError("Unable to submit. Please try again.");
                return null;
            } finally {
                setSubmitting(false);
            }
        },
        [invitationCode],
    );

    const submitCurrentAudioAndProgress = React.useCallback(
        async (
            audioBlob: Blob | undefined,
            transcript: string | undefined,
            nextIndex: number,
        ) => {
            const q = currentQuestion;
            if (!q) return null;

            const t = (transcript ?? "").trim();

            if (audioBlob) {
                attachAudioToQuestion(q.id, audioBlob, t || undefined);
            }

            if (t) setAnswer(q.id, t);

            const basePayload: PublicSubmitAudioPayload = {
                invitation_code: invitationCode.trim(),
                question_id: q.id,
                current_index: nextIndex,
            };

            const audioTooLarge = Boolean(audioBlob) &&
                (audioBlob!.size ?? 0) > MAX_AUDIO_UPLOAD_BYTES;

            if (audioTooLarge) {
                if (t) {
                    const res = await submitPartial({
                        answers: [{ question_id: q.id, answer: t }],
                        current_index: nextIndex,
                    });
                    if (!res) {
                        await submitPartial({ answers: [], current_index: nextIndex });
                    }
                    return res;
                }
                return await submitPartial({ answers: [], current_index: nextIndex });
            }

            const payload: PublicSubmitAudioPayload = { ...basePayload };
            if (audioBlob) payload.audio_blob = audioBlob;
            if (t) payload.transcript_text = t;

            const audioRes = await submitAudio(payload);
            if (audioRes) return audioRes;

            if (t) {
                const res = await submitPartial({
                    answers: [{ question_id: q.id, answer: t }],
                    current_index: nextIndex,
                });
                if (res) return res;
            }

            return await submitPartial({ answers: [], current_index: nextIndex });
        },
        [
            attachAudioToQuestion,
            currentQuestion,
            invitationCode,
            setAnswer,
            submitAudio,
            submitPartial,
        ],
    );

    const updateProgress = React.useCallback(
        async (nextIndex: number) => {
            return await submitPartial({ answers: [], current_index: nextIndex });
        },
        [submitPartial],
    );

    const submitCurrentTextAndProgress = React.useCallback(
        async (text: string, nextIndex: number) => {
            const q = currentQuestion;
            if (!q) return null;

            const answerText = String(text ?? "").trim();
            setAnswer(q.id, answerText);

            return await submitPartial({
                answers: [{ question_id: q.id, answer: answerText }],
                current_index: nextIndex,
            });
        },
        [currentQuestion, setAnswer, submitPartial],
    );

    /** Navigate to a specific question from review */
    const goToQuestion = React.useCallback((index: number) => {
        const clamped = clampIndex(index, questions.length);
        setCurrentIndex(clamped);
        setShowReview(false);
    }, [questions.length]);

    /** Complete the flow via backend */
    const completeFlow = React.useCallback(async () => {
        const code = invitationCode.trim();
        if (!code) return;

        setSubmitting(true);
        setError(null);

        try {
            await firstPrinciplesPublicApi.submitAnswers(code, {
                answers: [],
                current_index: questions.length - 1,
            });
        } catch (e) {
            console.error("Failed to complete flow", e);
            setError("Unable to submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }, [invitationCode, questions.length]);

    return {
        invitationCode,
        setInvitationCode,

        loading,
        submitting,
        error,
        success,
        setError,

        state,
        questions,
        answers,
        audioDrafts,

        isLoaded,
        isLastQuestion,

        currentIndex,
        setCurrentIndex,
        currentQuestion,

        canGoPrev,
        canGoNext,

        showReview,
        setShowReview,
        goToQuestion,
        completeFlow,

        load,

        updateProgress,
        submitPartial,

        submitCurrentTextAndProgress,
        submitCurrentAudioAndProgress,

        clearAudioForQuestion,
    };
}
